import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { PedidosNuevosAPI } from '../../services/pedidosNuevosService';
import {
  PedidoListaItem,
  FiltrosPedidosNuevos,
  RespuestaListadoPedidos,
  EstadoPedidoNuevo,
} from '../../types/pedidosNuevos';
import TarjetaPedido from '../TarjetaPedido';
import ToggleVencidos from './ToggleVencidos';
import { useNavigate } from 'react-router-dom';
import { getHoyLocal } from '../../utils/fechaActual';
import {
  Search,
  RefreshCw,
  Plus,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

const getAyerLocal = (): string => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Tab para que los clientes vean sus propios pedidos
 * También usado por admin cuando quiere ver sus pedidos personales
 */
const MisPedidosTab: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pedidos, setPedidos] = useState<PedidoListaItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Paginación y filtros
  const [paginaActual, setPaginaActual] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [totalPedidos, setTotalPedidos] = useState(0);
  const [filtroEstado, setFiltroEstado] = useState<EstadoPedidoNuevo | ''>('');
  const [busqueda, setBusqueda] = useState('');
  const [modoVencidos, setModoVencidos] = useState(false);

  const cargarPedidos = async () => {
    if (!user) return;

    // Si no es cliente, no cargar pedidos
    if (user.rol !== 'cliente') {
      setPedidos([]);
      setTotalPaginas(0);
      setTotalPedidos(0);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const filtros: FiltrosPedidosNuevos = {
        clienteId: user.id, // Solo mis pedidos
        estado: filtroEstado || undefined,
        busqueda: busqueda || undefined,
        page: paginaActual,
        limit: 10,
      };
      if (modoVencidos) {
        filtros.fechaHasta = getAyerLocal();
      } else {
        filtros.fechaDesde = getHoyLocal();
      }

      const respuesta: RespuestaListadoPedidos =
        await PedidosNuevosAPI.listarPedidos(filtros);

      setPedidos(respuesta.data);
      setTotalPaginas(respuesta.pagination.totalPages);
      setTotalPedidos(respuesta.pagination.total);
    } catch (err: any) {
      console.error('Error al cargar pedidos:', err);
      setError(err.message || 'Error al cargar los pedidos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarPedidos();
  }, [user, paginaActual, filtroEstado, modoVencidos]);

  const handleBuscar = () => {
    setPaginaActual(1); // Reset a primera página
    cargarPedidos();
  };

  const handleVerDetalle = (pedido: PedidoListaItem) => {
    navigate(`/pedidos/${pedido.id}`);
  };

  const handleCrearNuevo = () => {
    navigate('/armar-pedido');
  };

  if (!user) return null;

  // Si no es cliente, mostrar mensaje
  if (user.rol !== 'cliente') {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">
          Esta sección está disponible solo para clientes.
        </p>
        <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
          Los administradores y chefs pueden gestionar pedidos desde "Pedidos
          Entrantes" o "Gestión Completa".
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header con acciones */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Mis Pedidos
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {totalPedidos} pedido{totalPedidos !== 1 ? 's' : ''} encontrado
            {totalPedidos !== 1 ? 's' : ''}
          </p>
        </div>

        <button
          onClick={handleCrearNuevo}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Nuevo Pedido</span>
        </button>
      </div>

      {/* Filtros y búsqueda */}
      <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Búsqueda */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Buscar
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleBuscar()}
                placeholder="Buscar por código, minuta..."
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              />
              <button
                onClick={handleBuscar}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Search className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Filtro por estado */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Estado
            </label>
            <select
              value={filtroEstado}
              onChange={(e) => {
                setFiltroEstado(e.target.value as EstadoPedidoNuevo | '');
                setPaginaActual(1);
              }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            >
              <option value="">Todos los estados</option>
              <option value="borrador">Borrador</option>
              <option value="solicitado">Solicitado</option>
              <option value="en_preparacion">En Cocina</option>
              <option value="listo">Listo</option>
              <option value="despachado">Despachado</option>
              <option value="cancelado">Cancelado</option>
            </select>
          </div>
        </div>

        {/* Botón refrescar */}
        <div className="flex justify-end">
          <button
            onClick={cargarPedidos}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refrescar</span>
          </button>
        </div>
      </div>

      {/* Toggle vigentes ↔ vencidos */}
      <ToggleVencidos
        modoVencidos={modoVencidos}
        onCambio={(v) => {
          setPaginaActual(1);
          setModoVencidos(v);
        }}
      />

      {/* Mensajes de estado */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {loading && (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      )}

      {/* Lista de pedidos */}
      {!loading && pedidos.length === 0 && (
        <div className="text-center py-12">
          {modoVencidos ? (
            <p className="text-gray-500 dark:text-gray-400 text-lg mb-4">
              No tenés pedidos vencidos.
            </p>
          ) : (
            <>
              <p className="text-gray-500 dark:text-gray-400 text-lg mb-4">
                No tienes pedidos vigentes todavía
              </p>
              <button
                onClick={handleCrearNuevo}
                className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
              >
                Crear tu primer pedido
              </button>
            </>
          )}
        </div>
      )}

      {!loading && pedidos.length > 0 && (
        <div className="space-y-4">
          {pedidos.map((pedido) => (
            <TarjetaPedido
              key={pedido.id}
              pedido={pedido}
              onVerDetalle={handleVerDetalle}
              mostrarCliente={false} // No mostrar cliente porque todos son del usuario actual
            />
          ))}
        </div>
      )}

      {/* Paginación */}
      {totalPaginas > 1 && (
        <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-4">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Página {paginaActual} de {totalPaginas}
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setPaginaActual((p) => Math.max(1, p - 1))}
              disabled={paginaActual === 1}
              className="flex items-center gap-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              <span>Anterior</span>
            </button>

            <button
              onClick={() =>
                setPaginaActual((p) => Math.min(totalPaginas, p + 1))
              }
              disabled={paginaActual === totalPaginas}
              className="flex items-center gap-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <span>Siguiente</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MisPedidosTab;
